import type { PublicUser } from "@rhema/client";
import { useCallback, useEffect, useState } from "react";
import { useApi } from "../session/session";

// Screens that change contacts announce it, so every list showing them reloads.
const changes = new EventTarget();
export function contactsChanged() {
  changes.dispatchEvent(new Event("change"));
}

/** Calls `listener` when contacts, blocks or reports change anywhere. Returns how to stop. */
export function onContactsChanged(listener: () => void): () => void {
  changes.addEventListener("change", listener);
  return () => changes.removeEventListener("change", listener);
}

export function useContacts() {
  const api = useApi();
  const [contacts, setContacts] = useState<PublicUser[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(() => {
    api
      .contacts()
      .then((list) => {
        setContacts(list);
        setError(null);
      })
      .catch((e: Error) => setError(e.message));
  }, [api]);

  useEffect(() => {
    reload();
    changes.addEventListener("change", reload);
    return () => changes.removeEventListener("change", reload);
  }, [reload]);

  return { contacts, error, reload };
}

/** Fetches someone's public profile, refreshed when contacts or blocks change. */
export function usePerson(id: string | undefined) {
  const api = useApi();
  const [person, setPerson] = useState<PublicUser | null>(null);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(() => {
    if (!id) return;
    api
      .user(id)
      .then((p) => {
        setPerson(p);
        setError(null);
      })
      .catch((e: Error) => setError(e.message));
  }, [api, id]);

  useEffect(() => {
    setPerson(null);
    reload();
    changes.addEventListener("change", reload);
    return () => changes.removeEventListener("change", reload);
  }, [reload]);

  return { person, error, reload };
}
